import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnchoredTooltip } from '../AnchoredTooltip';
import type { Prompt, User } from '../../types';
import { getScoreBadgeClass } from '../../utils/scoreTier';
import { formatTimestamp } from '../../utils/formatTimestamp';
import './PromptsTable.css';

export interface PaginationDisplay {
  page: number;
  per_page: number;
  total_count: number;
}

export interface IdeOption {
  value: string;
  label: string;
}

export interface ScoreFilterChange {
  minScore?: string;
  maxScore?: string;
}

export interface TimestampFilterChange {
  fromDate?: string;
  fromTime?: string;
  toDate?: string;
  toTime?: string;
}

export type SortColumn = 'score' | 'timestamp';
export type SortOrder = 'asc' | 'desc';

export interface PromptsTableProps {
  prompts: Prompt[];
  loading?: boolean;
  hasMore: boolean;
  loadingMore?: boolean;
  onLoadMore: () => void;
  pagination?: PaginationDisplay | null;
  /** User filter: dropdown with search (name or email); API still filters by email */
  users?: User[];
  usersLoading?: boolean;
  selectedEmails?: string[];
  onUserFilterChange?: (emails: string[]) => void;
  /** When true, user column shows the signed-in scope only (no multi-user picker). */
  userFilterLocked?: boolean;
  /** IDE filter: simple dropdown */
  ideOptions?: readonly IdeOption[];
  selectedIde?: string;
  onIdeFilterChange?: (ide: string) => void;
  /** Score filter: min/max in popover */
  minScore?: string;
  maxScore?: string;
  onScoreFilterChange?: (values: ScoreFilterChange) => void;
  /** Timestamp filter: from/to datetime in popover */
  fromDate?: string;
  fromTime?: string;
  toDate?: string;
  toTime?: string;
  onTimestampFilterChange?: (values: TimestampFilterChange) => void;
  /** Sort: first click asc, second desc */
  sortBy?: SortColumn | null;
  sortOrder?: SortOrder;
  onSortChange?: (column: SortColumn) => void;
}

const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/** Format IDE enum value to sentence case (e.g. CURSOR -> Cursor, ANTIGRAVITY -> Antigravity). */
const formatIdeSentenceCase = (ide: string | null | undefined): string => {
  if (ide == null || ide === '') return '—';
  return ide.charAt(0).toUpperCase() + ide.slice(1).toLowerCase();
};

/** Format task_category for display (e.g. Correction_Major -> Correction Major). */
const formatTaskCategory = (category: string | null | undefined): string => {
  if (category == null || category === '') return '—';
  return String(category).replace(/_/g, ' ');
};

/** Dropdown label: "Full Name (email)" or email if no name */
const userFilterDisplayLabel = (u: User): string => {
  const parts = [u.first_name, u.last_name].filter(Boolean);
  const name = parts.join(' ').trim();
  if (name) return `${name} (${u.email})`;
  return u.email;
};

/** Get task category from prompt (API sends task_category; fallback to score_details.category). */
const getTaskCategory = (prompt: Prompt): string | null | undefined => {
  return prompt.task_category ?? null;
};

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const SortAscIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

const SortDescIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12l7 7 7-7" />
  </svg>
);

const SortNoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
  </svg>
);

const DEFAULT_IDE_OPTIONS: readonly IdeOption[] = [
  { value: '', label: 'All IDEs' },
  { value: 'CURSOR', label: 'Cursor' },
  { value: 'KIRO', label: 'Kiro' },
  { value: 'COPILOT', label: 'Copilot' },
  { value: 'ANTIGRAVITY', label: 'Antigravity' },
];

export const PromptsTable: React.FC<PromptsTableProps> = ({
  prompts,
  loading = false,
  hasMore,
  loadingMore = false,
  onLoadMore,
  pagination,
  users = [],
  usersLoading = false,
  selectedEmails = [],
  onUserFilterChange,
  userFilterLocked = false,
  ideOptions = DEFAULT_IDE_OPTIONS,
  selectedIde = '',
  onIdeFilterChange,
  minScore = '',
  maxScore = '',
  onScoreFilterChange,
  fromDate = '',
  fromTime = '00:00',
  toDate = '',
  toTime = '23:59',
  onTimestampFilterChange,
  sortBy = null,
  sortOrder = 'asc',
  onSortChange,
}) => {
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scorePopoverRef = useRef<HTMLDivElement>(null);
  const timestampPopoverRef = useRef<HTMLDivElement>(null);
  const userFilterRef = useRef<HTMLDivElement>(null);
  const ideFilterRef = useRef<HTMLDivElement>(null);

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [ideDropdownOpen, setIdeDropdownOpen] = useState(false);
  const [scorePopoverOpen, setScorePopoverOpen] = useState(false);
  const [timestampPopoverOpen, setTimestampPopoverOpen] = useState(false);
  const [draftMinScore, setDraftMinScore] = useState(minScore);
  const [draftMaxScore, setDraftMaxScore] = useState(maxScore);
  const [draftFromDate, setDraftFromDate] = useState(fromDate);
  const [draftFromTime, setDraftFromTime] = useState(fromTime);
  const [draftToDate, setDraftToDate] = useState(toDate);
  const [draftToTime, setDraftToTime] = useState(toTime);

  useEffect(() => {
    if (!scorePopoverOpen) return;
    setDraftMinScore(minScore);
    setDraftMaxScore(maxScore);
  }, [scorePopoverOpen, minScore, maxScore]);

  useEffect(() => {
    if (!timestampPopoverOpen) return;
    setDraftFromDate(fromDate);
    setDraftFromTime(fromTime);
    setDraftToDate(toDate);
    setDraftToTime(toTime);
  }, [timestampPopoverOpen, fromDate, fromTime, toDate, toTime]);

  const selectedUserLabel = useMemo(() => {
    if (!selectedEmails || selectedEmails.length === 0) return 'All Users';
    if (selectedEmails.length === 1) return selectedEmails[0];
    return `${selectedEmails.length} selected`;
  }, [selectedEmails]);

  // Filter users by name or email (for dropdown list)
  const filteredUsersForFilter = useMemo(() => {
    if (!userSearch.trim()) return users;
    const q = userSearch.trim().toLowerCase();
    return users.filter((u) => {
      const label = userFilterDisplayLabel(u).toLowerCase();
      return label.includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, userSearch]);

  // Close dropdowns/popovers on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (userFilterRef.current?.contains(target)) return;
      if (ideFilterRef.current?.contains(target)) return;
      if (scorePopoverRef.current?.contains(target)) return;
      if (timestampPopoverRef.current?.contains(target)) return;
      setUserDropdownOpen(false);
      setIdeDropdownOpen(false);
      setScorePopoverOpen(false);
      setTimestampPopoverOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Infinite scroll: when sentinel is visible and hasMore and not loading, load more
  useEffect(() => {
    if (!hasMore || loadingMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: null, rootMargin: '100px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, onLoadMore]);

  const handleRowClick = (promptId: string) => {
    navigate(`/prompts/${promptId}`);
  };

  const handleSortClick = (column: SortColumn) => {
    if (!onSortChange) return;
    if (sortBy === column) {
      onSortChange(column); // parent toggles asc/desc
    } else {
      onSortChange(column); // parent sets column and asc
    }
  };

  const renderHeaderUser = () => (
    <th className="prompts-table__header prompts-table__header--user">
      <div className="prompts-table__header-inner">
        <span className="prompts-table__header-label">User</span>
        {onUserFilterChange && userFilterLocked && (
          <span
            className={`prompts-table__header-dropdown-trigger prompts-table__header-dropdown-trigger--locked ${(selectedEmails && selectedEmails.length > 0) ? 'prompts-table__header-dropdown-trigger--active' : ''}`}
            title="Showing only your prompts"
          >
            {selectedEmails?.length === 1 ? selectedEmails[0] : 'Your prompts'}
          </span>
        )}
        {onUserFilterChange && !userFilterLocked && (
          <div className="prompts-table__header-filter-wrap" ref={userFilterRef}>
            <button
              type="button"
              className={`prompts-table__header-dropdown-trigger ${(selectedEmails && selectedEmails.length > 0) ? 'prompts-table__header-dropdown-trigger--active' : ''}`}
              onClick={() => {
                setUserDropdownOpen((o) => !o);
                setIdeDropdownOpen(false);
              }}
              aria-expanded={userDropdownOpen}
              aria-haspopup="listbox"
            >
              {selectedUserLabel}
            </button>
            {userDropdownOpen && (
              <div className="prompts-table__header-dropdown" role="listbox">
                <div className="prompts-table__selected-pills">
                  {(selectedEmails || []).map((email) => (
                    <span key={email} className="prompts-table__pill">
                      <span className="prompts-table__pill-text">{email}</span>
                      <button
                        type="button"
                        className="prompts-table__pill-remove"
                        onClick={() => {
                          const next = (selectedEmails || []).filter((e) => e !== email);
                          onUserFilterChange(next);
                        }}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="prompts-table__header-dropdown-search"
                  placeholder="Search name or email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="prompts-table__header-dropdown-option"
                  onClick={() => {
                    onUserFilterChange([]);
                    setUserDropdownOpen(false);
                    setUserSearch('');
                  }}
                >
                  All Users
                </button>
                {filteredUsersForFilter.map((u) => (
                  <button
                    key={u.email}
                    type="button"
                    className={`prompts-table__header-dropdown-option ${(selectedEmails || []).includes(u.email) ? 'prompts-table__header-dropdown-option--selected' : ''}`}
                    onClick={() => {
                      const exists = (selectedEmails || []).includes(u.email);
                      const next = exists
                        ? (selectedEmails || []).filter((e) => e !== u.email)
                        : [...(selectedEmails || []), u.email];
                      onUserFilterChange(next);
                    }}
                  >
                    {userFilterDisplayLabel(u)}
                  </button>
                ))}
                {filteredUsersForFilter.length === 0 && users.length > 0 && (
                  <div className="prompts-table__header-dropdown-note">
                    No matches — try a different search
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );

  const renderHeaderIde = () => (
    <th className="prompts-table__header prompts-table__header--ide">
      <div className="prompts-table__header-inner">
        <span className="prompts-table__header-label">IDE</span>
        {onIdeFilterChange && (
          <div className="prompts-table__header-filter-wrap" ref={ideFilterRef}>
            <button
              type="button"
              className={`prompts-table__header-dropdown-trigger ${selectedIde ? 'prompts-table__header-dropdown-trigger--active' : ''}`}
              onClick={() => {
                setIdeDropdownOpen((o) => !o);
                setUserDropdownOpen(false);
              }}
              aria-expanded={ideDropdownOpen}
              aria-haspopup="listbox"
            >
              {ideOptions.find((o) => o.value === selectedIde)?.label ?? 'All IDEs'}
            </button>
            {ideDropdownOpen && (
              <div className="prompts-table__header-dropdown" role="listbox">
                {ideOptions.map((opt) => (
                  <button
                    key={opt.value || 'all'}
                    type="button"
                    className={`prompts-table__header-dropdown-option ${selectedIde === opt.value ? 'prompts-table__header-dropdown-option--selected' : ''}`}
                    onClick={() => {
                      onIdeFilterChange(opt.value);
                      setIdeDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );

  const renderHeaderScore = () => (
    <th className="prompts-table__header prompts-table__header--score">
      <div className="prompts-table__header-inner prompts-table__header-inner--with-sort">
        <span className="prompts-table__header-label">Score</span>
        <div className="prompts-table__header-actions">
          {onScoreFilterChange && (
            <div className="prompts-table__header-popover-wrap" ref={scorePopoverRef}>
              <button
                type="button"
                className={`prompts-table__header-icon-btn ${minScore || maxScore ? 'prompts-table__header-icon-btn--active' : ''}`}
                onClick={() => {
                  setScorePopoverOpen((o) => !o);
                  setTimestampPopoverOpen(false);
                }}
                aria-label="Filter by score range"
                title="Filter by score"
              >
                <FilterIcon />
              </button>
              {scorePopoverOpen && (
                <div className="prompts-table__header-popover">
                  <div className="prompts-table__header-popover-title">Score range</div>
                  <div className="prompts-table__header-popover-row">
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={0.1}
                      placeholder="Min"
                      className="prompts-table__header-popover-input"
                      value={draftMinScore}
                      onChange={(e) => setDraftMinScore(e.target.value)}
                    />
                    <span className="prompts-table__header-popover-sep">–</span>
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={0.1}
                      placeholder="Max"
                      className="prompts-table__header-popover-input"
                      value={draftMaxScore}
                      onChange={(e) => setDraftMaxScore(e.target.value)}
                    />
                  </div>
                  <div className="prompts-table__header-popover-actions">
                    <button
                      type="button"
                      className="prompts-table__header-popover-apply"
                      onClick={() => {
                        onScoreFilterChange({ minScore: draftMinScore, maxScore: draftMaxScore });
                        setScorePopoverOpen(false);
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {onSortChange && (
            <button
              type="button"
              className={`prompts-table__header-icon-btn ${sortBy === 'score' ? 'prompts-table__header-icon-btn--active' : ''}`}
              onClick={() => handleSortClick('score')}
              aria-label={sortBy === 'score' ? `Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}` : 'Sort by score'}
              title="Sort by score"
            >
              {sortBy === 'score' ? (sortOrder === 'asc' ? <SortAscIcon /> : <SortDescIcon />) : <SortNoneIcon />}
            </button>
          )}
        </div>
      </div>
    </th>
  );

  const renderHeaderTimestamp = () => (
    <th className="prompts-table__header prompts-table__header--timestamp">
      <div className="prompts-table__header-inner prompts-table__header-inner--with-sort">
        <span className="prompts-table__header-label">Timestamp</span>
        <div className="prompts-table__header-actions">
          {onTimestampFilterChange && (
            <div className="prompts-table__header-popover-wrap" ref={timestampPopoverRef}>
              <button
                type="button"
                className={`prompts-table__header-icon-btn ${fromDate || toDate ? 'prompts-table__header-icon-btn--active' : ''}`}
                onClick={() => {
                  setTimestampPopoverOpen((o) => !o);
                  setScorePopoverOpen(false);
                }}
                aria-label="Filter by date range"
                title="Filter by date"
              >
                <FilterIcon />
              </button>
              {timestampPopoverOpen && (
                <div className="prompts-table__header-popover prompts-table__header-popover--wide">
                  <div className="prompts-table__header-popover-title">From</div>
                  <div className="prompts-table__header-popover-row">
                    <input
                      type="date"
                      className="prompts-table__header-popover-input"
                      value={draftFromDate}
                      onChange={(e) => setDraftFromDate(e.target.value)}
                    />
                    <input
                      type="time"
                      className="prompts-table__header-popover-input prompts-table__header-popover-input--time"
                      value={draftFromTime}
                      onChange={(e) => setDraftFromTime(e.target.value)}
                    />
                  </div>
                  <div className="prompts-table__header-popover-title">To</div>
                  <div className="prompts-table__header-popover-row">
                    <input
                      type="date"
                      className="prompts-table__header-popover-input"
                      value={draftToDate}
                      onChange={(e) => setDraftToDate(e.target.value)}
                    />
                    <input
                      type="time"
                      className="prompts-table__header-popover-input prompts-table__header-popover-input--time"
                      value={draftToTime}
                      onChange={(e) => setDraftToTime(e.target.value)}
                    />
                  </div>
                  <div className="prompts-table__header-popover-actions">
                    <button
                      type="button"
                      className="prompts-table__header-popover-apply"
                      onClick={() => {
                        onTimestampFilterChange({
                          fromDate: draftFromDate,
                          fromTime: draftFromTime,
                          toDate: draftToDate,
                          toTime: draftToTime,
                        });
                        setTimestampPopoverOpen(false);
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {onSortChange && (
            <button
              type="button"
              className={`prompts-table__header-icon-btn ${sortBy === 'timestamp' ? 'prompts-table__header-icon-btn--active' : ''}`}
              onClick={() => handleSortClick('timestamp')}
              aria-label={sortBy === 'timestamp' ? `Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}` : 'Sort by date'}
              title="Sort by date"
            >
              {sortBy === 'timestamp' ? (sortOrder === 'asc' ? <SortAscIcon /> : <SortDescIcon />) : <SortNoneIcon />}
            </button>
          )}
        </div>
      </div>
    </th>
  );

  const shown = prompts.length;
  const total = pagination?.total_count ?? prompts.length;

  // When no rows: still show table with sticky header and filters, and one empty-state row
  const displayPrompts = prompts;
  const isEmpty = !displayPrompts.length;
  const showInitialSkeleton = loading && isEmpty;

  return (
    <div className="prompts-table-container">
      <div className="prompts-table-scroll">
        <div className="prompts-table-wrapper">
          <table className="prompts-table">
            <thead>
              <tr>
                {renderHeaderUser()}
                {renderHeaderIde()}
                <th className="prompts-table__header prompts-table__header--category">Category</th>
                <th className="prompts-table__header prompts-table__header--prompt">Prompt</th>
                {renderHeaderScore()}
                {renderHeaderTimestamp()}
              </tr>
            </thead>
            <tbody>
              {showInitialSkeleton ? (
                [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <tr key={i} className="prompts-table__skeleton-row">
                    <td><span className="prompts-table__skeleton-cell" /></td>
                    <td><span className="prompts-table__skeleton-cell" /></td>
                    <td><span className="prompts-table__skeleton-cell" /></td>
                    <td><span className="prompts-table__skeleton-cell prompts-table__skeleton-cell--wide" /></td>
                    <td><span className="prompts-table__skeleton-cell prompts-table__skeleton-cell--narrow" /></td>
                    <td><span className="prompts-table__skeleton-cell" /></td>
                  </tr>
                ))
              ) : isEmpty ? (
                <tr className="prompts-table__empty-row">
                  <td colSpan={6} className="prompts-table__empty-cell">
                    <div className="prompts-table__empty-inline">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                        <path d="M9 12h6M9 16h6M9 8h6M5 4h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
                      </svg>
                      <h3>No prompts match your filters</h3>
                      <p>Try adjusting or clearing filters to see more results.</p>
                    </div>
                  </td>
                </tr>
              ) : (
              displayPrompts.map((prompt) => (
                <tr
                  key={prompt.id}
                  className="prompts-table__row--clickable"
                  onClick={() => handleRowClick(prompt.id)}
                >
                  <td className="prompts-table__cell prompts-table__cell--user">
                    <span className="prompts-table__email">{prompt.email || '—'}</span>
                  </td>
                  <td className="prompts-table__cell prompts-table__cell--ide">
                    <span className="prompts-table__ide">
                      {formatIdeSentenceCase(prompt.ide)}
                    </span>
                  </td>
                  <td className="prompts-table__cell prompts-table__cell--category">
                    <span className="prompts-table__category">
                      {formatTaskCategory(getTaskCategory(prompt))}
                    </span>
                  </td>
                  <td className="prompts-table__cell prompts-table__cell--prompt">
                    {prompt.prompt_text ? (
                      <AnchoredTooltip content={prompt.prompt_text}>
                        <p className="prompts-table__prompt-text">
                          {truncateText(prompt.prompt_text, 120)}
                        </p>
                      </AnchoredTooltip>
                    ) : (
                      <p className="prompts-table__prompt-text">—</p>
                    )}
                  </td>
                  <td className="prompts-table__cell prompts-table__cell--score">
                    <span className={`score-badge ${getScoreBadgeClass(prompt.overall_score)}`}>
                      {prompt.overall_score !== null ? prompt.overall_score.toFixed(2) : 'Unscored'}
                    </span>
                  </td>
                  <td className="prompts-table__cell prompts-table__cell--timestamp">
                    <span className="prompts-table__timestamp">
                      {formatTimestamp(prompt.timestamp)}
                    </span>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>

        <div ref={sentinelRef} className="prompts-table__sentinel" aria-hidden="true" />

        {loadingMore && (
          <div className="prompts-table__loading-more">
            <div className="prompts-table__spinner prompts-table__spinner--small" />
            <p>Loading more...</p>
          </div>
        )}
      </div>
      <div className="prompts-table__footer">
        <span className="prompts-table__count">
          Showing {shown} of {total} prompts
        </span>
      </div>
    </div>
  );
};

export default PromptsTable;
